import { useEffect, useMemo, useRef, useState } from 'react';
import {
    Upload, FileText, Loader2, ChevronDown, X, Search,
    CheckCircle2, ArrowLeft, Save, Plus, Sparkles,
} from 'lucide-react';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { QuestionCard } from './QuestionCard';
import { SampleDownloadButtons } from './SampleDownloadButtons';
import * as apiClient from '@/services/apiClient';
import { cn } from '@/lib/utils';

const PARSE_MSGS = [
    'Reading your file…',
    'Extracting questions…',
    'AI filling in missing answers…',
    'Generating scoring keywords…',
    'Almost ready…',
];

// ── Minimal searchable select (same pattern as NewQuestionSetModal) ────────────
function SearchableSelect({ id, label, value, onChange, options, placeholder, disabled, required }) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const filtered = useMemo(() => {
        const q = query.toLowerCase();
        return q ? options.filter(o => o.label.toLowerCase().includes(q)) : options;
    }, [options, query]);
    const selected = options.find(o => o.value === value);
    useEffect(() => {
        if (!open) return;
        const close = (e) => { if (!e.target.closest(`[data-ss="${id}"]`)) setOpen(false); };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, [open, id]);
    return (
        <div className="space-y-1.5" data-ss={id}>
            {label && <Label htmlFor={id}>{label}{required && <span className="text-destructive ml-0.5">*</span>}</Label>}
            <div className="relative">
                <button id={id} type="button" disabled={disabled}
                    onClick={() => !disabled && setOpen(v => !v)}
                    className="flex h-10 w-full items-center justify-between rounded-md border bg-background px-3 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50">
                    <span className={!selected ? 'text-muted-foreground' : ''}>
                        {selected?.label || placeholder}
                    </span>
                    <ChevronDown className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} />
                </button>
                {open && (
                    <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-lg">
                        <div className="flex items-center gap-2 border-b px-3 py-2">
                            <Search className="h-3.5 w-3.5 text-muted-foreground" />
                            <input autoFocus className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                                placeholder="Search…" value={query} onChange={e => setQuery(e.target.value)} />
                            {query && <button type="button" onClick={() => setQuery('')}><X className="h-3 w-3 text-muted-foreground" /></button>}
                        </div>
                        <div className="max-h-48 overflow-y-auto py-1">
                            {filtered.length === 0
                                ? <p className="px-3 py-4 text-center text-xs text-muted-foreground">No options found.</p>
                                : filtered.map(o => (
                                    <button key={o.value} type="button"
                                        onClick={() => { onChange(o.value); setOpen(false); setQuery(''); }}
                                        className={`flex w-full flex-col items-start px-3 py-2 text-sm hover:bg-muted/60 ${value === o.value ? 'bg-primary/10 font-medium' : ''}`}>
                                        {o.label}
                                        {o.sub && <span className="text-xs text-muted-foreground">{o.sub}</span>}
                                    </button>
                                ))
                            }
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

/**
 * UploadQuestionSetModal — 3-step flow:
 *   1. Drop/select file + JD selector
 *   2. Parsing + AI enrichment (loading)
 *   3. Review & edit parsed questions, then Save
 *
 * Props:
 *   open          boolean
 *   onClose       () => void
 *   templates     InterviewTemplate[]
 *   masterTypes   InterviewMaster[]
 *   masterDiffs   InterviewMaster[]
 *   onCreated     (newSet) => void
 */
export function UploadQuestionSetModal({ open, onClose, templates, masterTypes, masterDiffs, onCreated }) {
    const [step,        setStep]        = useState(1); // 1 | 2 | 3
    const [file,        setFile]        = useState(null);
    const [dragOver,    setDragOver]    = useState(false);
    const [templateId,  setTemplateId]  = useState('');
    const [typeId,      setTypeId]      = useState('');
    const [diffId,      setDiffId]      = useState('');
    const [setName,     setSetName]     = useState('');
    const [parseMsg,    setParseMsg]    = useState('');
    const [questions,   setQuestions]   = useState([]);
    const [dirty,       setDirty]       = useState(false);
    const [error,       setError]       = useState('');
    const [saving,      setSaving]      = useState(false);
    const fileInputRef = useRef(null);
    let msgInterval = null;

    const reset = () => {
        setStep(1); setFile(null); setTemplateId(''); setTypeId(''); setDiffId('');
        setSetName(''); setQuestions([]); setDirty(false); setError(''); setSaving(false);
    };

    useEffect(() => { if (!open) reset(); }, [open]);

    // ── File selection ────────────────────────────────────────────────────────
    const acceptFile = (f) => {
        if (!f) return;
        const ext = f.name.split('.').pop().toLowerCase();
        if (!['pdf', 'doc', 'docx', 'txt', 'xls', 'xlsx'].includes(ext)) {
            setError('Only PDF, DOCX, DOC, TXT, and XLSX files are accepted.');
            return;
        }
        setFile(f);
        setError('');
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        acceptFile(e.dataTransfer.files[0]);
    };

    // ── Parse ─────────────────────────────────────────────────────────────────
    const handleParse = async () => {
        if (!file)       { setError('Please select a file.'); return; }
        if (!templateId) { setError('Please select a job description.'); return; }
        setError('');
        setStep(2);
        let mi = 0;
        setParseMsg(PARSE_MSGS[mi]);
        msgInterval = setInterval(() => {
            mi = (mi + 1) % PARSE_MSGS.length;
            setParseMsg(PARSE_MSGS[mi]);
        }, 2600);

        try {
            const formData = new FormData();
            formData.append('file', file);
            const res = await apiClient.parseUploadedQuestionSet(formData);
            clearInterval(msgInterval);
            setQuestions((res.data || []).map((q, i) => ({ ...q, order: i })));
            setStep(3);
        } catch (err) {
            clearInterval(msgInterval);
            setError(err.message || 'Failed to parse file.');
            setStep(1);
        }
    };

    // ── Question editor helpers ───────────────────────────────────────────────
    const applyEdit = (idx, updated) => {
        setQuestions(prev => prev.map((q, i) => i === idx ? { ...q, ...updated } : q));
        setDirty(true);
    };

    const deleteQuestion = (idx) => {
        setQuestions(prev => prev.filter((_, i) => i !== idx).map((q, i) => ({ ...q, order: i })));
        setDirty(true);
    };

    const moveUp = (idx) => {
        if (idx === 0) return;
        setQuestions(prev => {
            const next = [...prev];
            [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
            return next.map((q, i) => ({ ...q, order: i }));
        });
        setDirty(true);
    };

    const moveDown = (idx) => {
        setQuestions(prev => {
            if (idx >= prev.length - 1) return prev;
            const next = [...prev];
            [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
            return next.map((q, i) => ({ ...q, order: i }));
        });
        setDirty(true);
    };

    const addBlank = () => {
        setQuestions(prev => [...prev, {
            questionText: '', questionType: 'technical', difficulty: 'medium',
            timeLimitSeconds: 300, idealAnswer: '', keywords: [],
            questionFormat: 'verbal', options: [], order: prev.length,
        }]);
        setDirty(true);
    };

    // ── Save ──────────────────────────────────────────────────────────────────
    const handleSave = async () => {
        if (questions.length === 0) { setError('Add at least one question before saving.'); return; }
        setSaving(true);
        setError('');
        try {
            const res = await apiClient.saveUploadedQuestionSet({
                templateId,
                name:                  setName.trim() || undefined,
                interviewTypeMasterId: typeId  || undefined,
                difficultyMasterId:    diffId  || undefined,
                questions,
            });
            onCreated?.(res.data);
            onClose();
            reset();
        } catch (err) {
            setError(err.message || 'Failed to save question set.');
        } finally {
            setSaving(false);
        }
    };

    // ── Options ───────────────────────────────────────────────────────────────
    const templateOpts = useMemo(() => templates.map(t => ({ value: t._id, label: t.title, sub: t.department || t.position || '' })), [templates]);
    const typeOpts     = useMemo(() => masterTypes.map(m => ({ value: m._id, label: m.name })), [masterTypes]);
    const diffOpts     = useMemo(() => masterDiffs.map(m => ({ value: m._id, label: m.name })), [masterDiffs]);

    return (
        <Dialog open={open} onOpenChange={v => !v && onClose()}>
            <DialogContent className="max-h-[92vh] w-[95vw] overflow-hidden flex flex-col sm:max-w-2xl p-0">

                {/* ── Header ── */}
                <DialogHeader className="px-6 pt-5 pb-4 pr-14 border-b border-border flex-shrink-0">
                    <DialogTitle className="flex items-center gap-2 text-base font-semibold">
                        <Upload className="h-4 w-4" />
                        {step === 1 && 'Upload Question Set'}
                        {step === 2 && 'Parsing file…'}
                        {step === 3 && `Review ${questions.length} question${questions.length !== 1 ? 's' : ''}`}
                    </DialogTitle>
                    <DialogDescription className="text-xs">
                        {step === 1 && 'Upload a PDF, DOCX, XLSX, or TXT file. AI fills in missing answers and keywords.'}
                        {step === 2 && parseMsg}
                        {step === 3 && 'Review and edit before saving. You can change any field.'}
                    </DialogDescription>
                </DialogHeader>

                {/* ── Step 1: Upload form ── */}
                {step === 1 && (
                    <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
                        {error && (
                            <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                                {error}
                            </div>
                        )}

                        {/* Drop zone */}
                        <div
                            onDrop={onDrop}
                            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                            onDragLeave={() => setDragOver(false)}
                            onClick={() => fileInputRef.current?.click()}
                            className={cn(
                                'cursor-pointer rounded-xl border-2 border-dashed px-6 py-10 text-center transition-colors',
                                dragOver
                                    ? 'border-primary bg-primary/5'
                                    : file
                                        ? 'border-emerald-500/50 bg-emerald-500/5'
                                        : 'border-border hover:border-primary/40 hover:bg-muted/30',
                            )}
                        >
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".pdf,.doc,.docx,.txt,.xls,.xlsx"
                                className="hidden"
                                onChange={e => acceptFile(e.target.files[0])}
                            />
                            {file ? (
                                <div className="flex flex-col items-center gap-2">
                                    <CheckCircle2 className="h-8 w-8 text-emerald-500" />
                                    <p className="text-sm font-medium text-foreground">{file.name}</p>
                                    <p className="text-xs text-muted-foreground">
                                        {(file.size / 1024).toFixed(0)} KB · Click to change
                                    </p>
                                </div>
                            ) : (
                                <div className="flex flex-col items-center gap-2">
                                    <FileText className="h-8 w-8 text-muted-foreground" />
                                    <p className="text-sm font-medium text-foreground">
                                        Drop your file here or click to browse
                                    </p>
                                    <p className="text-xs text-muted-foreground">PDF, DOCX, XLSX, or TXT · Max 5 MB</p>
                                </div>
                            )}
                        </div>

                        {/* Sample file */}
                        <div className="rounded-lg border border-border bg-muted/30 px-3 py-2.5 space-y-2">
                            <div className="flex items-center gap-2">
                                <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                                <p className="text-xs text-muted-foreground">
                                    Not sure about the format? Download a sample file:
                                </p>
                            </div>
                            <SampleDownloadButtons label="" size="xs" />
                        </div>

                        {/* JD selector */}
                        <SearchableSelect
                            id="uqs-template" label="Job Description" required
                            value={templateId} onChange={setTemplateId}
                            options={templateOpts} placeholder="Select a job description…"
                        />

                        {/* Optional: type + difficulty */}
                        <div className="grid grid-cols-2 gap-3">
                            <SearchableSelect
                                id="uqs-type" label="Interview Type (optional)"
                                value={typeId} onChange={setTypeId}
                                options={typeOpts} placeholder="e.g. Technical…"
                            />
                            <SearchableSelect
                                id="uqs-diff" label="Difficulty (optional)"
                                value={diffId} onChange={setDiffId}
                                options={diffOpts} placeholder="e.g. Medium…"
                            />
                        </div>

                        {/* Set name */}
                        <div className="space-y-1.5">
                            <Label htmlFor="uqs-name">Set name (optional)</Label>
                            <Input id="uqs-name" value={setName}
                                onChange={e => setSetName(e.target.value)}
                                placeholder="Auto-generated if left blank" />
                        </div>

                        <div className="flex justify-end gap-2 pt-1">
                            <Button variant="outline" onClick={onClose}>Cancel</Button>
                            <Button onClick={handleParse} disabled={!file || !templateId} className="gap-1.5">
                                <Sparkles className="h-4 w-4" /> Parse & Enrich with AI
                            </Button>
                        </div>
                    </div>
                )}

                {/* ── Step 2: Loading ── */}
                {step === 2 && (
                    <div className="flex flex-1 flex-col items-center justify-center gap-4 py-20">
                        <Loader2 className="h-10 w-10 animate-spin text-primary" />
                        <p className="text-sm text-muted-foreground">{parseMsg}</p>
                    </div>
                )}

                {/* ── Step 3: Review & edit ── */}
                {step === 3 && (
                    <>
                        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
                            {error && (
                                <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                                    {error}
                                </div>
                            )}

                            {questions.map((q, idx) => (
                                <QuestionCard
                                    key={q._id ? String(q._id) : `upload-${idx}`}
                                    question={q}
                                    index={idx}
                                    total={questions.length}
                                    onEdit={(upd) => applyEdit(idx, upd)}
                                    onDelete={() => deleteQuestion(idx)}
                                    onMoveUp={() => moveUp(idx)}
                                    onMoveDown={() => moveDown(idx)}
                                    onRegen={() => {}}
                                    regenLoading={false}
                                    disabled={saving}
                                />
                            ))}

                            <button
                                type="button"
                                onClick={addBlank}
                                disabled={saving}
                                className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-border py-3 text-sm text-muted-foreground hover:border-primary/40 hover:text-foreground transition-colors"
                            >
                                <Plus className="h-4 w-4" /> Add question manually
                            </button>
                        </div>

                        <div className="flex items-center justify-between border-t border-border px-6 py-4 flex-shrink-0">
                            <Button variant="ghost" size="sm" className="gap-1.5"
                                onClick={() => { setStep(1); setError(''); }} disabled={saving}>
                                <ArrowLeft className="h-3.5 w-3.5" /> Back
                            </Button>
                            <Button size="sm" className="gap-1.5" onClick={handleSave}
                                disabled={saving || questions.length === 0}>
                                {saving
                                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                    : <Save className="h-3.5 w-3.5" />}
                                Save {questions.length} question{questions.length !== 1 ? 's' : ''}
                            </Button>
                        </div>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}

export default UploadQuestionSetModal;
